import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from './reducer';
import {
  makeSelectShelvesSelected,
  makeSelectShelvesSelectedLastCabinet,
} from 'containers/Configurator/selectors/selection';
import {
  ISDEPTH,
  MINIMAL_HEIGHT_FOR_ADDING_IS_SUGGESTION,
} from 'containers/IntermediateShelfPanel/constants';
import { findISCombinations } from 'lib/modelBuilder/utils/intermediateShelfTools';
import { makeSelectCabinetDepth } from 'containers/Configurator/selectors/furniture';
import _ from 'lodash';
import { IntermediateShelf } from 'lib/modelBuilder/pieces/IntermediateShelf';
import { Color } from 'lib/modelBuilder/entities/Color';

/**
 * Direct selector to the intermediateShelfPanel state domain
 */

const selectIntermediateShelfPanelDomain = (state: ApplicationRootState) =>
  state.intermediateShelfPanel || initialState;

export const makeSelectIsVisibleIntermediateShelfPanel = () =>
  createSelector(selectIntermediateShelfPanelDomain, substate => {
    return substate.isVisible;
  });

export const makeSelectISSelection = () =>
  createSelector(
    selectIntermediateShelfPanelDomain,
    substate => substate.selectedLevelIS,
  );

export const makeSelectIsSameIntermediateShelvesInShelvesSelected = () =>
  createSelector(makeSelectShelvesSelected(), (shelvesSelected): boolean => {
    let counterIS = -1;
    for (const shelf of shelvesSelected) {
      if (counterIS !== shelf.intermediatesShelves.length) {
        if (counterIS === -1) counterIS = shelf.intermediatesShelves.length;
        else {
          return false;
        }
      }
    }
    return true;
  });

export const makeSelectCounterSelectedISInShelvesSelected = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectIsSameIntermediateShelvesInShelvesSelected(),
    (shelvesSelected, sameISNumber): number | null => {
      if (!sameISNumber || shelvesSelected.length === 0) return null;
      return shelvesSelected[0].intermediatesShelves.length;
    },
  );

export const makeSelectIsSameDepthIntermediateShelvesInShelvesSelected = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectCounterSelectedISInShelvesSelected(),
    (shelvesSelected, counterIS): boolean => {
      if (!counterIS) return false;
      for (const i of _.range(0, counterIS)) {
        const depth = shelvesSelected[0].intermediatesShelves[i].depth;
        for (const shelf of shelvesSelected) {
          if (shelf.intermediatesShelves[i].depth !== depth) {
            return false;
          }
        }
      }

      return true;
    },
  );

export const makeSelectHeightIS = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectIsSameIntermediateShelvesInShelvesSelected(),
    makeSelectISSelection(),
    (shelvesSelected, sameISNumber, selection): number | null => {
      // Only one selected IS is allow to modify height
      if (
        !sameISNumber ||
        shelvesSelected.length === 0 ||
        shelvesSelected[0].intermediatesShelves.length === 0 ||
        selection.length !== 1 ||
        !shelvesSelected[0].intermediatesShelves[selection[0]]
      )
        return null;
      return shelvesSelected[0].intermediatesShelves[selection[0]].height;
    },
  );

export const makeSelectDepthIS = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectIsSameIntermediateShelvesInShelvesSelected(),
    makeSelectISSelection(),
    (shelvesSelected, sameISNumber, selection): number | null => {
      // Only one selected IS is allow to modify depth
      if (
        !sameISNumber ||
        shelvesSelected.length === 0 ||
        !shelvesSelected[0].intermediatesShelves ||
        shelvesSelected[0].intermediatesShelves.length === 0 ||
        selection.length !== 1
      )
        return null;

      const value = shelvesSelected[0].intermediatesShelves[selection[0]];
      if (value) {
        return value.depth;
      }
      return null;
    },
  );

export const makeSelectCommonColorsIS = () =>
  createSelector(
    makeSelectShelvesSelectedLastCabinet(),
    makeSelectCounterSelectedISInShelvesSelected(),
    (shelvesSelected, counterIs): (Color | undefined)[] | undefined => {
      if (!counterIs || counterIs === 0) {
        return undefined;
      }
      const colorsIS: (Color | undefined)[] = [];
      for (const i of _.range(0, counterIs)) {
        for (const shelf of shelvesSelected) {
          const entry = shelf.getIntermediateShelf(i);
          if (colorsIS[i]) {
            if (colorsIS[i] !== entry?.interShelf.color) {
              colorsIS[i] = undefined;
              break;
            }
          } else {
            colorsIS[i] = entry?.interShelf.color;
          }
        }
      }

      return colorsIS;
    },
  );

export const makeSelectCommonColorIS = () =>
  createSelector(
    makeSelectCommonColorsIS(),
    makeSelectShelvesSelected(),
    makeSelectISSelection(),
    (colors, shelvesSelected, selection): Color | undefined => {
      if (shelvesSelected.length === 0) return undefined;
      let commonColor = undefined;
      if (colors) {
        for (const shelf of shelvesSelected) {
          for (const i of selection) {
            const entryIs = shelf.intermediatesShelves[i];
            if (entryIs) {
              if (!commonColor) {
                commonColor = entryIs.interShelf.color;
              }
              if (entryIs.interShelf.color.id !== commonColor.id) {
                return undefined;
              }
            }
          }
        }
      }
      return commonColor;
    },
  );

export const makeSelectCanAddIS = (
  height: number = MINIMAL_HEIGHT_FOR_ADDING_IS_SUGGESTION,
) => {
  return createSelector(
    makeSelectShelvesSelected(),
    makeSelectCounterSelectedISInShelvesSelected(),
    (shelvesSelected, counterIs): boolean => {
      if (shelvesSelected && shelvesSelected.length > 0) {
        const shelf = shelvesSelected[0];

        const max = counterIs ? counterIs + 1 : 1;
        const ISCombo = findISCombinations(shelf.height, max);
        return !!ISCombo;
      }
      return false;
    },
  );
};

export const makeSelectDepthMaxIS = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectIsSameDepthIntermediateShelvesInShelvesSelected(),
    makeSelectISSelection(),
    makeSelectCabinetDepth(),
    (
      shelvesSelected,
      isSameDepth,
      selectedLevelIS,
      cabinetDepth,
    ): number | null => {
      if (!isSameDepth) return null;

      let minDepth = cabinetDepth;
      const shelf = shelvesSelected[0];
      const firstPosIs = selectedLevelIS.sort(
        (a: number, b: number) => a - b,
      )[0];
      if (firstPosIs === 0) return cabinetDepth;
      for (const entryIs of shelf.intermediatesShelves.filter(
        e => e.position < firstPosIs,
      )) {
        if (entryIs.depth < minDepth) minDepth = entryIs.depth;
      }
      return minDepth;
    },
  );

export const makeSelectAllowISHeight = () =>
  createSelector(
    makeSelectShelvesSelected(),
    makeSelectIsSameIntermediateShelvesInShelvesSelected(),
    makeSelectISSelection(),
    (shelvesSelected, sameISNumber, selection): number[] => {
      if (!sameISNumber) return [];
      const disallowHeight: number[] = [];

      for (const shelf of shelvesSelected) {
        for (const indexIS of selection) {
          for (const h of [125, 175, 225]) {
            if (!IntermediateShelf.checkAllowHeight(indexIS, shelf, h)) {
              if (!disallowHeight.includes(h)) disallowHeight.push(h);
            }
          }
        }
      }

      return disallowHeight;
    },
  );

export const makeSelectDepthValuesIS = () =>
  createSelector(makeSelectCabinetDepth(), (cabinetDepth): number[] => {
    return ISDEPTH.filter(e => e <= cabinetDepth);
  });
